/**
 * Обзор меню для менеджера/владельца — корень /menu (design manager v3 §9.1).
 *
 * Воспроизводит мокап 9.1 «Меню ресторана — обзор»:
 *   - 4 KPI: Позиций / Категорий / Заказов сегодня / Выручка нед. (с дельтами);
 *   - card «Разделы»: навигация в Категории / Позиции / Заказы по ресторану /
 *     Остатки кухни с counts;
 *   - card «Хиты недели»: top-5 позиций по числу заказов + чип лидера выручки;
 *   - опц. low-stock баннер.
 * Мобайл 375: KPI горизонтальным скроллом, «Разделы» списком (rows ≥44px),
 * FAB «+». Пусто: «Меню ещё пустое — добавьте первую позицию или импортируйте».
 *
 * Данные — useMenuOverview (KPI/хиты считаются на клиенте; никаких выдуманных
 * чисел: нет данных → 0/«—»). Источник заказов/выручки — лента заявок отеля.
 */
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import { formatPriceMinor } from '@hsc/types';
import { useMenuOverview } from './useMenu.js';

const LOCALE = 'ru';

/**
 * Одна KPI-плитка обзора. Кликабельна (§9.1): «Позиций»/«Категорий» ведут в
 * управление меню, «Заказов сегодня» — в очередь кухня/бар. Рендерим как
 * <button>, чтобы карточка целиком была интерактивной (фокус-ринг, клавиатура).
 */
function Kpi({
  label,
  value,
  delta,
  testid,
  onClick,
}: {
  label: string;
  value: string;
  delta?: string;
  testid: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button type="button" className="menu-kpi" data-testid={testid} onClick={onClick}>
      <div className="menu-kpi__label">{label}</div>
      <div className="menu-kpi__value">{value}</div>
      {delta !== undefined && <div className="menu-kpi__delta">{delta}</div>}
    </button>
  );
}

export function MenuOverview(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMenuOverview();

  if (isLoading) {
    return (
      <section data-testid="menu-overview">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Меню ресторана</h1>
        </div>
        <div className="menu-kpi-row" data-testid="menu-overview-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={72} radius={12} />
          ))}
        </div>
        <Skeleton height={180} radius={12} />
      </section>
    );
  }

  if (isError || data === undefined) {
    return (
      <section data-testid="menu-overview">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Меню ресторана</h1>
        </div>
        <Card>
          <p role="alert" data-testid="menu-overview-error">
            Не удалось загрузить обзор меню. Попробуйте обновить страницу.
          </p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  if (data.categories === 0 && data.items === 0) {
    return (
      <section data-testid="menu-overview">
        <div className="ta-topbar">
          <h1 className="hsc-page__title">Меню ресторана</h1>
          <Button variant="primary" size="sm" onClick={() => navigate('/menu/categories')}>
            + Категория
          </Button>
        </div>
        <Card>
          <p data-testid="menu-overview-empty">
            Меню ещё пустое — добавьте первую позицию или импортируйте.
          </p>
        </Card>
      </section>
    );
  }

  const revenue = formatPriceMinor(data.revenueWeekMinor, data.currency, LOCALE);

  return (
    <section data-testid="menu-overview">
      <div className="ta-topbar">
        <div>
          <div className="ta-crumbs">Управление / Меню</div>
          <h1 className="hsc-page__title">Меню ресторана</h1>
        </div>
      </div>

      {data.lowStock > 0 && (
        <div className="menu-lowstock" role="status" data-testid="menu-overview-lowstock">
          На кухне заканчивается {data.lowStock} ингредиент(ов) — проверьте остатки.
        </div>
      )}

      {/* 4 KPI (§9.1 grid-4): кликабельны. «Позиций»/«Категорий» → управление
          меню, «Заказов сегодня»/«Выручка» → очередь заказов кухня/бар. */}
      <div className="menu-kpi-row" data-testid="menu-overview-kpi">
        <Kpi
          label="Позиций"
          value={String(data.items)}
          testid="menu-kpi-items"
          onClick={() => navigate('/menu/items')}
        />
        <Kpi
          label="Категорий"
          value={String(data.categories)}
          testid="menu-kpi-cats"
          onClick={() => navigate('/menu/categories')}
        />
        <Kpi
          label="Заказов сегодня"
          value={String(data.ordersToday)}
          {...(data.ordersActive > 0 ? { delta: `${data.ordersActive} активных` } : {})}
          testid="menu-kpi-orders"
          onClick={() => navigate('/menu/orders')}
        />
        <Kpi
          label="Выручка нед."
          value={revenue}
          testid="menu-kpi-revenue"
          onClick={() => navigate('/menu/orders')}
        />
      </div>

      <div className="menu-overview-grid">
        <Card>
          <h2 className="menu-card__title">Разделы</h2>
          <ul className="menu-sections" data-testid="menu-overview-sections">
            <li>
              <button
                type="button"
                className="menu-section-row"
                data-testid="menu-section-categories"
                onClick={() => navigate('/menu/categories')}
              >
                <span>Категории</span>
                <span className="chip">{data.categories}</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="menu-section-row"
                data-testid="menu-section-items"
                onClick={() => navigate('/menu/items')}
              >
                <span>Позиции</span>
                <span className="chip">{data.items}</span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="menu-section-row"
                data-testid="menu-section-orders"
                onClick={() => navigate('/menu/orders')}
              >
                <span>Заказы по ресторану</span>
                <span className="chip chip--ok">
                  {data.ordersToday} · {data.ordersActive} активных
                </span>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="menu-section-row"
                data-testid="menu-section-inventory"
                onClick={() => navigate('/inventory')}
              >
                <span>Остатки кухни</span>
                <span className="chip">{data.lowStock > 0 ? `${data.lowStock} low` : '—'}</span>
              </button>
            </li>
          </ul>
        </Card>

        <Card>
          <h2 className="menu-card__title">Хиты недели</h2>
          {data.hits.length === 0 ? (
            <p className="ta-muted" data-testid="menu-overview-hits-empty">
              Пока нет заказов за неделю — данные появятся, когда гости начнут
              заказывать.
            </p>
          ) : (
            <ul className="menu-hits" data-testid="menu-overview-hits">
              {data.hits.map((h) => (
                <li className="menu-hit-row" key={h.itemId} data-testid={`menu-hit-${h.itemId}`}>
                  <span>
                    {h.name}
                    {h.revenueLeader === true && (
                      <span className="chip chip--ok menu-hit__leader">выручка №1</span>
                    )}
                  </span>
                  <strong>{h.orders}</strong>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* FAB «+» (мобайл 375): быстрый переход к добавлению позиции. */}
      <button
        type="button"
        className="menu-fab"
        aria-label="Добавить позицию"
        data-testid="menu-overview-fab"
        onClick={() => navigate('/menu/items')}
      >
        +
      </button>
    </section>
  );
}

export default MenuOverview;
